/**
 * BalanceReport — Balansrapport (Balance Sheet Report)
 *
 * Features:
 * - Fiscal year and monthly period selection
 * - Previous year comparison column (includeComparison)
 * - Verification drill-down per account (includeVerifications)
 * - SUMMA TILLGÅNGAR / SUMMA EK&S lines with balance equation check
 * - Report metadata (fiscal year state, IB source, generation timestamp)
 * - PDF export with all features
 * - Collapsible section hierarchy per K2 structure
 */

import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Alert, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faFileDownload } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { useParams, useHistory, Link } from 'react-router-dom';
import Select from 'react-select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PageHeader from '../../../layouts/PageHeader';
import { Scale } from 'lucide-react';
import { NoData } from '../../SharedComponents/NoData';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';
import './BalanceReport.css';

// Display names for top-level section keys (backend returns ASCII keys)
const SECTION_DISPLAY_NAMES = {
    'TILLGANGAR': 'TILLGÅNGAR',
    'EGET KAPITAL OCH SKULDER': 'EGET KAPITAL OCH SKULDER',
};

const BalanceReport = () => {
    const [fiscalYears, setFiscalYears] = useState([]);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [yearPeriodOptions, setYearPeriodOptions] = useState([]);
    const [showAllYears, setShowAllYears] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Report data
    const [currentYearReport, setCurrentYearReport] = useState(null);
    const [previousYearReport, setPreviousYearReport] = useState(null);
    const [currentFiscalYearInfo, setCurrentFiscalYearInfo] = useState(null);
    const [previousFiscalYearInfo, setPreviousFiscalYearInfo] = useState(null);
    const [validation, setValidation] = useState(null);
    const [metadata, setMetadata] = useState(null);

    // UI state
    const [expandedSections, setExpandedSections] = useState({
        'TILLGANGAR': true,
        'EGET KAPITAL OCH SKULDER': true,
    });
    const [showVerifications, setShowVerifications] = useState(false);
    const [expandedAccounts, setExpandedAccounts] = useState({});
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [selectedVerification, setSelectedVerification] = useState(null);
    const [formalMode, setFormalMode] = useState(false); // Balansräkning ÅRL view

    const history = useHistory();
    const { companyId } = useParams();

    // In formal mode, show previous year UB column. In working mode, no previous year.
    const hasPreviousYear = formalMode && previousYearReport !== null;

    // -----------------------------------------------------------------------
    // Fetch fiscal years
    // -----------------------------------------------------------------------
    useEffect(() => { getFiscalYears(); }, [companyId]);

    const getFiscalYears = async () => {
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
            setFiscalYears(result.data.fiscalYears);
            if (result.data.fiscalYears.length > 0) {
                const latestFiscalYear = result.data.fiscalYears.sort((a, b) => b.year - a.year)[0];
                setSelectedFiscalYear(latestFiscalYear);
            }
        } catch (err) {
            setError('Kunde inte hämta räkenskapsår');
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Build period dropdown (same pattern as ResultReport)
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!fiscalYears || fiscalYears.length === 0) return;

        const monthFormatter = new Intl.DateTimeFormat('sv-SE', { month: 'long' });
        const toLocalYMD = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const sorted = [...fiscalYears].sort((a, b) => b.year - a.year);
        const limited = showAllYears ? sorted : sorted.slice(0, 5);

        const groups = limited.map((fy) => {
            const fyStart = new Date(fy.start_date);
            const fyEnd = new Date(fy.end_date);
            const opts = [];
            opts.push({
                value: { fiscalYear: fy, period: { type: 'year', startDate: fy.start_date, endDate: fy.end_date } },
                label: 'Hela året',
            });

            let cursor = new Date(fyStart.getFullYear(), fyStart.getMonth(), 1);
            while (cursor <= fyEnd) {
                const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
                const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
                const start = monthStart < fyStart ? fyStart : monthStart;
                const end = monthEnd > fyEnd ? fyEnd : monthEnd;
                const monthName = monthFormatter.format(monthStart);
                const label = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${monthStart.getFullYear()}`;
                opts.push({
                    value: { fiscalYear: fy, period: { type: 'month', startDate: toLocalYMD(start), endDate: toLocalYMD(end) } },
                    label,
                });
                cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
            }
            return { label: String(fy.year), options: opts };
        });

        const hasMore = !showAllYears && sorted.length > limited.length;
        const moreGroup = hasMore ? [{ label: '', options: [{ value: { action: '__more__' }, label: 'Visa fler år…' }] }] : [];
        setYearPeriodOptions([...groups, ...moreGroup]);

        const latestGroup = groups[0];
        if (latestGroup && latestGroup.options.length > 0) {
            const def = latestGroup.options[0];
            setSelectedFiscalYear(def.value.fiscalYear);
            setSelectedPeriod({ value: def.value.period, label: def.label });
        }
    }, [fiscalYears, showAllYears]);

    // -----------------------------------------------------------------------
    // Fetch balance report
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (selectedFiscalYear && selectedPeriod) getBalanceReport();
    }, [selectedFiscalYear, selectedPeriod, showVerifications, formalMode]);

    const getBalanceReport = async () => {
        if (!selectedFiscalYear) return;
        setLoading(true);

        const isWholeYear = selectedPeriod?.value?.type === 'year';
        const queryParams = {
            fiscalYearId: selectedFiscalYear.fiscal_year_id,
            includeComparison: formalMode && isWholeYear ? 'true' : 'false',
            includeVerifications: !formalMode && showVerifications ? 'true' : 'false',
        };
        if (!isWholeYear) {
            queryParams.startDate = selectedPeriod.value.startDate;
            queryParams.endDate = selectedPeriod.value.endDate;
        }

        try {
            const response = await axios.get(getApiUrl(`${companyId}/reporting/balance`), {
                params: queryParams,
                withCredentials: true,
            });

            const data = response.data;

            if (data.current_year) {
                // Comparison response
                setCurrentYearReport(data.current_year.report);
                setCurrentFiscalYearInfo(data.current_year.fiscal_year);
                setValidation(data.current_year.report.validation);
                setMetadata(data.current_year.report._metadata);

                if (data.previous_year) {
                    setPreviousYearReport(data.previous_year.report);
                    setPreviousFiscalYearInfo(data.previous_year.fiscal_year);
                } else {
                    setPreviousYearReport(null);
                    setPreviousFiscalYearInfo(null);
                }
            } else {
                // Non-comparison response (backward compat)
                setCurrentYearReport(data);
                setCurrentFiscalYearInfo(null);
                setValidation(data.validation);
                setMetadata(data._metadata);
                setPreviousYearReport(null);
                setPreviousFiscalYearInfo(null);
            }

            setError(null);
        } catch (err) {
            setError('Kunde inte hämta balansrapport');
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Formatting
    // -----------------------------------------------------------------------
    /**
     * Format amount for display, respecting the active mode:
     *
     * Working mode: raw trial balance signs (assets +, EK&S -), 2 decimals (öre)
     * Formal mode (ÅRL): EK&S negated, hela kronor (0 decimals) per BFNAR 2016:10 p 2.3
     *
     * @param {number|string} amount - The raw value
     * @param {boolean} isEkSection - Whether this value belongs to EK&S side
     */
    const decimals = formalMode ? 0 : 2;
    const zeroDisplay = formalMode ? '0' : '0,00';

    const formatAmount = (amount, isEkSection = false) => {
        if (!amount && amount !== 0) return zeroDisplay;
        let num = parseFloat(amount);
        if (isNaN(num)) return zeroDisplay;
        if (formalMode && isEkSection) num = -num;
        return new Intl.NumberFormat('sv-SE', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            style: 'decimal',
        }).format(num);
    };

    // -----------------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------------
    const handleYearPeriodChange = (opt) => {
        if (!opt || !opt.value) return;
        if (opt.value.action === '__more__') { setShowAllYears(true); return; }
        setSelectedFiscalYear(opt.value.fiscalYear);
        setSelectedPeriod({ value: opt.value.period, label: opt.label });
    };

    const toggleSection = (path) => {
        setExpandedSections(prev => ({ ...prev, [path]: !prev[path] }));
    };

    const toggleAccount = (accountId) => {
        setExpandedAccounts(prev => ({ ...prev, [accountId]: !prev[accountId] }));
    };

    const handleVerificationClick = async (verification) => {
        if (!verification?.verification_id) return;
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/verifications/${verification.verification_id}`),
                { withCredentials: true }
            );
            setSelectedVerification(response.data);
            setShowVerificationModal(true);
        } catch (err) {
            setSelectedVerification({
                verification_id: verification.verification_id,
                series: verification.verification_number?.charAt(0) || 'A',
                number: verification.verification_number?.slice(1) || verification.verification_number,
                date: verification.date,
                description: verification.description,
                transactions: [],
            });
            setShowVerificationModal(true);
        }
    };

    // -----------------------------------------------------------------------
    // Pre-computed previous year closing balance map (O(1) lookups)
    // Built once when previousYearReport changes, avoids O(N*M) recursive search
    // -----------------------------------------------------------------------
    const prevYearBalanceMap = React.useMemo(() => {
        if (!previousYearReport) return new Map();
        const map = new Map();
        const collectAccounts = (section) => {
            if (!section || typeof section !== 'object') return;
            for (const value of Object.values(section)) {
                if (value && value.accounts && Array.isArray(value.accounts)) {
                    value.accounts.forEach(a => {
                        if (a.account_number) map.set(a.account_number, a.closing_balance);
                    });
                } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                    collectAccounts(value);
                }
            }
        };
        collectAccounts(previousYearReport.TILLGANGAR);
        collectAccounts(previousYearReport['EGET KAPITAL OCH SKULDER']);
        return map;
    }, [previousYearReport]);

    const getPreviousYearClosingBalance = (accountNumber) => {
        return prevYearBalanceMap.get(accountNumber) || null;
    };

    // -----------------------------------------------------------------------
    // Render account row (with optional verification expansion)
    // -----------------------------------------------------------------------
    const renderAccountRow = (account, indent, isEk = false) => {
        if (!account) return null;
        const hasVer = showVerifications && account.verifications && account.verifications.length > 0;
        const isAccExpanded = expandedAccounts[account.account_id];
        const prevYearUB = hasPreviousYear ? getPreviousYearClosingBalance(account.account_number) : null;

        const rows = [];
        rows.push(
            <tr key={account.account_id} className={hasVer ? 'cursor-pointer' : ''}>
                <td style={{ paddingLeft: `${indent}rem` }}>
                    <div className="account-info" onClick={() => hasVer && toggleAccount(account.account_id)}>
                        {hasVer && (
                            <span style={{ color: '#666', width: '16px', marginRight: '0.25rem' }}>
                                <FontAwesomeIcon icon={isAccExpanded ? faChevronDown : faChevronRight} size="sm" />
                            </span>
                        )}
                        <span className="account-number">{account.account_number}</span>
                        <span className="account-name">{account.account_name}</span>
                        {hasVer && (
                            <span className="verification-count">({account.verifications.length})</span>
                        )}
                    </div>
                </td>
                {formalMode ? (
                    <>
                        <td>{formatAmount(account.closing_balance, isEk)}</td>
                        {hasPreviousYear && (
                            <td style={{ color: '#6c757d' }}>{prevYearUB !== null ? formatAmount(prevYearUB, isEk) : ''}</td>
                        )}
                    </>
                ) : (
                    <>
                        <td>{formatAmount(account.opening_balance, isEk)}</td>
                        <td>{formatAmount(account.change, isEk)}</td>
                        <td>{formatAmount(account.closing_balance, isEk)}</td>
                    </>
                )}
            </tr>
        );

        if (hasVer && isAccExpanded) {
            account.verifications.forEach((ver, idx) => {
                rows.push(
                    <tr key={`ver-${account.account_id}-${idx}`} className="verification-row">
                        <td style={{ paddingLeft: '4rem' }}>
                            <div className="verification-info">
                                <small className="verification-details">
                                    <span className="verification-number" onClick={() => handleVerificationClick(ver)}>
                                        {ver.verification_number}
                                    </span>
                                    <span className="verification-date">{new Date(ver.date).toLocaleDateString('sv-SE')}</span>
                                    <span className="verification-description">{ver.description}</span>
                                </small>
                            </div>
                        </td>
                        {formalMode ? (
                            <>
                                <td><small style={{ color: '#666' }}>{formatAmount(ver.transaction_type === 'debit' ? ver.amount : -ver.amount)}</small></td>
                                {hasPreviousYear && <td></td>}
                            </>
                        ) : (
                            <>
                                <td></td>
                                <td><small style={{ color: '#666' }}>{formatAmount(ver.transaction_type === 'debit' ? ver.amount : -ver.amount)}</small></td>
                                <td></td>
                            </>
                        )}
                    </tr>
                );
            });
        }

        return rows;
    };

    // -----------------------------------------------------------------------
    // Render a subsection (expandable category with accounts)
    // Depth controls indentation: 0 = directly under main header, 1 = nested
    // -----------------------------------------------------------------------
    const renderSubsection = (sectionData, sectionName, parentPath, depth, isEk = false) => {
        if (!sectionData || typeof sectionData !== 'object') return null;

        const currentPath = parentPath ? `${parentPath}.${sectionName}` : sectionName;
        const hasAccounts = sectionData.accounts && Array.isArray(sectionData.accounts) && sectionData.accounts.length > 0;
        const childSections = Object.entries(sectionData)
            .filter(([key, value]) => typeof value === 'object' &&
                !['accounts', 'regex', 'total_opening_balance', 'total_change', 'total_closing_balance'].includes(key) &&
                typeof value !== 'string');
        const hasChildren = hasAccounts || childSections.length > 0;
        const isExpanded = expandedSections[currentPath] !== false;
        const indent = depth === 0 ? 1.0 : 2.0;

        // Previous year total for this subsection
        let prevYearTotal = null;
        if (hasPreviousYear && previousYearReport) {
            const prevSection = findSectionInReport(previousYearReport, currentPath);
            if (prevSection && prevSection.total_closing_balance !== undefined) {
                prevYearTotal = prevSection.total_closing_balance;
            }
        }

        const rows = [];

        // Subsection header (expandable)
        rows.push(
            <tr
                key={`hdr-${currentPath}`}
                className={hasChildren ? 'cursor-pointer' : ''}
                onClick={() => hasChildren && toggleSection(currentPath)}
            >
                <td style={{ paddingLeft: `${indent}rem` }}>
                    {hasChildren && (
                        <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                            size="xs"
                            style={{ color: '#999', marginRight: '0.5rem', width: '12px' }}
                        />
                    )}
                    <strong>{sectionName}</strong>
                </td>
                {formalMode ? (
                    <>
                        <td></td>
                        {hasPreviousYear && <td></td>}
                    </>
                ) : (
                    <>
                        <td></td>
                        <td></td>
                        <td></td>
                    </>
                )}
            </tr>
        );

        // Expanded content
        if (isExpanded) {
            // Render child subsections first
            childSections.forEach(([key, value]) => {
                rows.push(...(renderSubsection(value, key, currentPath, depth + 1, isEk) || []));
            });

            // Render accounts
            if (hasAccounts) {
                sectionData.accounts.forEach(account => {
                    rows.push(...(renderAccountRow(account, 3.0, isEk) || []));
                });
            }
        }

        // Summa row for this subsection (always visible if it has totals)
        if (sectionData.total_closing_balance !== undefined) {
            rows.push(
                <tr key={`sum-${currentPath}`} style={{ fontWeight: '600' }}>
                    <td style={{ paddingLeft: `${indent}rem` }}>
                        Summa {sectionName.toLowerCase()}
                    </td>
                    {formalMode ? (
                        <>
                            <td>{formatAmount(sectionData.total_closing_balance, isEk)}</td>
                            {hasPreviousYear && (
                                <td style={{ color: '#6c757d' }}>{prevYearTotal !== null ? formatAmount(prevYearTotal, isEk) : ''}</td>
                            )}
                        </>
                    ) : (
                        <>
                            <td>{formatAmount(sectionData.total_opening_balance, isEk)}</td>
                            <td>{formatAmount(sectionData.total_change, isEk)}</td>
                            <td>{formatAmount(sectionData.total_closing_balance, isEk)}</td>
                        </>
                    )}
                </tr>
            );
        }

        return rows;
    };

    // -----------------------------------------------------------------------
    // Render a main section (TILLGÅNGAR or EK&S — non-expandable header)
    // -----------------------------------------------------------------------
    const renderMainSection = (report, sectionKey) => {
        const sectionData = report?.[sectionKey];
        if (!sectionData || typeof sectionData !== 'object') return null;

        const displayName = SECTION_DISPLAY_NAMES[sectionKey] || sectionKey;
        const isEk = sectionKey === 'EGET KAPITAL OCH SKULDER';
        const rows = [];

        // Main section header (NOT expandable, always shown)
        rows.push(
            <tr key={`main-${sectionKey}`}>
                <td colSpan={colCount} style={{ paddingTop: '1rem' }}>
                    <strong>{displayName}</strong>
                </td>
            </tr>
        );

        // Render child subsections
        const childSections = Object.entries(sectionData)
            .filter(([key]) =>
                !['total_opening_balance', 'total_change', 'total_closing_balance'].includes(key) &&
                typeof sectionData[key] === 'object'
            );

        childSections.forEach(([key, value]) => {
            rows.push(...(renderSubsection(value, key, sectionKey, 0, isEk) || []));
        });

        // SUMMA row for the main section
        if (sectionData.total_closing_balance !== undefined) {
            const prevTotal = previousYearReport?.[sectionKey]?.total_closing_balance;
            rows.push(
                <tr key={`summa-${sectionKey}`} className="summa-row">
                    <td><strong>{`SUMMA ${displayName}`}</strong></td>
                    {formalMode ? (
                        <>
                            <td><strong>{formatAmount(sectionData.total_closing_balance, isEk)}</strong></td>
                            {hasPreviousYear && (
                                <td style={{ color: '#6c757d' }}>
                                    <strong>{prevTotal !== undefined ? formatAmount(prevTotal, isEk) : ''}</strong>
                                </td>
                            )}
                        </>
                    ) : (
                        <>
                            <td><strong>{formatAmount(sectionData.total_opening_balance, isEk)}</strong></td>
                            <td><strong>{formatAmount(sectionData.total_change, isEk)}</strong></td>
                            <td><strong>{formatAmount(sectionData.total_closing_balance, isEk)}</strong></td>
                        </>
                    )}
                </tr>
            );
        }

        return rows;
    };

    // Helper to find a section in the report by dot-separated path.
    // Handles section names containing dots (e.g., "Varulager m.m.")
    const findSectionInReport = (report, path) => {
        if (!report) return null;
        // Direct lookup first (top-level keys like "TILLGANGAR")
        if (report[path]) return report[path];

        // Known section names containing dots
        const NAMES_WITH_DOTS = ['Varulager m.m.', 'Rörelseintäkter, lagerförändringar m.m.'];

        const smartSplit = (p) => {
            for (const name of NAMES_WITH_DOTS) {
                if (p.startsWith(name + '.')) {
                    return [name, ...p.substring(name.length + 1).split('.')];
                }
                if (p === name) return [name];
                // Also check if name appears mid-path
                const idx = p.indexOf('.' + name);
                if (idx >= 0) {
                    const before = p.substring(0, idx).split('.');
                    const after = p.substring(idx + 1 + name.length);
                    const rest = after.startsWith('.') ? after.substring(1).split('.') : [];
                    return [...before, name, ...rest.filter(Boolean)];
                }
            }
            return p.split('.');
        };

        const parts = smartSplit(path);
        let current = report;
        for (const part of parts) {
            if (!current || !current[part]) return null;
            current = current[part];
        }
        return current;
    };

    // -----------------------------------------------------------------------
    // PDF Export
    // -----------------------------------------------------------------------
    const exportToPDF = () => {
        const doc = new jsPDF();

        doc.setFontSize(14);
        doc.text(formalMode ? 'Balansräkning' : 'Balansrapport', 14, 10);
        doc.setFontSize(11);
        doc.text(`Räkenskapsår: ${selectedFiscalYear?.year || ''}`, 14, 18);
        const periodLabel = selectedPeriod?.label || 'Hela året';
        doc.text(`Period: ${periodLabel}`, 14, 25);

        const columns = formalMode
            ? ['', periodEnd ? new Date(periodEnd).toLocaleDateString('sv-SE') : String(selectedFiscalYear?.year || ''),
               ...(hasPreviousYear ? [previousFiscalYearInfo?.end_date ? new Date(previousFiscalYearInfo.end_date).toLocaleDateString('sv-SE') : String(previousFiscalYearInfo?.year || '')] : [])]
            : ['', 'Ingående balans', 'Förändring', 'Utgående balans'];

        // Format a value for PDF, respecting sign convention and decimal precision
        const pdfVal = (val, isEk) => {
            if (!val && val !== 0) return '';
            let num = parseFloat(val);
            if (isNaN(num)) return '';
            if (formalMode && isEk) num = -num;
            return formalMode ? Math.round(num).toLocaleString('sv-SE') : num.toFixed(2);
        };

        const buildSectionRows = (section, isEk, depth = 0, prevSection = null) => {
            if (!section || typeof section !== 'object') return [];
            let rows = [];

            Object.keys(section).forEach((key) => {
                const sub = section[key];
                if (!sub || typeof sub !== 'object') return;
                if (key === 'total_opening_balance' || key === 'total_change' || key === 'total_closing_balance') return;

                const isLeaf = sub.accounts && Array.isArray(sub.accounts) && sub.accounts.length > 0;
                rows.push([{ content: `${' '.repeat(depth * 2)}${key}`, colSpan: columns.length, styles: { halign: 'left', fontStyle: 'bold' } }]);

                if (isLeaf) {
                    sub.accounts.forEach((account) => {
                        const row = [`${' '.repeat((depth + 1) * 2)}${account.account_number} - ${account.account_name}`];
                        if (formalMode) {
                            row.push(pdfVal(account.closing_balance, isEk));
                            if (hasPreviousYear) {
                                const prevUB = getPreviousYearClosingBalance(account.account_number);
                                row.push(prevUB !== null ? pdfVal(prevUB, isEk) : '—');
                            }
                        } else {
                            row.push(pdfVal(account.opening_balance, isEk), pdfVal(account.change, isEk), pdfVal(account.closing_balance, isEk));
                        }
                        rows.push(row);
                    });

                    const totalRow = [{ content: `${' '.repeat(depth * 2)}Summa ${key}`, colSpan: 1, styles: { fontStyle: 'bold' } }];
                    if (formalMode) {
                        totalRow.push({ content: pdfVal(sub.total_closing_balance, isEk), styles: { fontStyle: 'bold' } });
                        if (hasPreviousYear) {
                            const prevSub = prevSection?.[key];
                            totalRow.push({ content: pdfVal(prevSub?.total_closing_balance, isEk), styles: { fontStyle: 'bold' } });
                        }
                    } else {
                        totalRow.push(
                            { content: pdfVal(sub.total_opening_balance, isEk), styles: { fontStyle: 'bold' } },
                            { content: pdfVal(sub.total_change, isEk), styles: { fontStyle: 'bold' } },
                            { content: pdfVal(sub.total_closing_balance, isEk), styles: { fontStyle: 'bold' } },
                        );
                    }
                    rows.push(totalRow);
                } else {
                    const prevSub = prevSection?.[key];
                    rows = rows.concat(buildSectionRows(sub, isEk, depth + 1, prevSub));

                    if (sub.total_closing_balance !== undefined) {
                        const parentTotalRow = [{ content: `${' '.repeat(depth * 2)}Summa ${key.toLowerCase()}`, colSpan: 1, styles: { fontStyle: 'bold' } }];
                        if (formalMode) {
                            parentTotalRow.push({ content: pdfVal(sub.total_closing_balance, isEk), styles: { fontStyle: 'bold' } });
                            if (hasPreviousYear) {
                                parentTotalRow.push({ content: pdfVal(prevSub?.total_closing_balance, isEk), styles: { fontStyle: 'bold' } });
                            }
                        } else {
                            parentTotalRow.push(
                                { content: pdfVal(sub.total_opening_balance, isEk), styles: { fontStyle: 'bold' } },
                                { content: pdfVal(sub.total_change, isEk), styles: { fontStyle: 'bold' } },
                                { content: pdfVal(sub.total_closing_balance, isEk), styles: { fontStyle: 'bold' } },
                            );
                        }
                        rows.push(parentTotalRow);
                    }
                }
            });

            return rows;
        };

        // TILLGÅNGAR
        doc.autoTable({
            head: [columns],
            body: [
                [{ content: 'Tillgångar', colSpan: columns.length, styles: { halign: 'left', fontStyle: 'bold' } }],
                ...buildSectionRows(currentYearReport?.TILLGANGAR, false, 0, previousYearReport?.TILLGANGAR),
            ],
            startY: 30,
            styles: { fontSize: 9 },
        });

        // SUMMA TILLGÅNGAR
        const tillgangarTotal = currentYearReport?.TILLGANGAR;
        if (tillgangarTotal) {
            const sumRow = [{ content: 'SUMMA TILLGÅNGAR', styles: { fontStyle: 'bold' } }];
            if (formalMode) {
                sumRow.push({ content: pdfVal(tillgangarTotal.total_closing_balance, false), styles: { fontStyle: 'bold' } });
                if (hasPreviousYear) sumRow.push({ content: pdfVal(previousYearReport?.TILLGANGAR?.total_closing_balance, false), styles: { fontStyle: 'bold' } });
            } else {
                sumRow.push(
                    { content: pdfVal(tillgangarTotal.total_opening_balance, false), styles: { fontStyle: 'bold' } },
                    { content: pdfVal(tillgangarTotal.total_change, false), styles: { fontStyle: 'bold' } },
                    { content: pdfVal(tillgangarTotal.total_closing_balance, false), styles: { fontStyle: 'bold' } },
                );
            }
            doc.autoTable({ body: [sumRow], startY: doc.lastAutoTable.finalY, styles: { fontSize: 9 } });
        }

        // EK & SKULDER
        const ekStartY = doc.lastAutoTable.finalY + 8;
        doc.autoTable({
            head: [columns],
            body: [
                [{ content: 'Eget Kapital och Skulder', colSpan: columns.length, styles: { halign: 'left', fontStyle: 'bold' } }],
                ...buildSectionRows(currentYearReport?.['EGET KAPITAL OCH SKULDER'], true, 0, previousYearReport?.['EGET KAPITAL OCH SKULDER']),
            ],
            startY: ekStartY,
            styles: { fontSize: 9 },
        });

        // SUMMA EK & SKULDER
        const ekTotal = currentYearReport?.['EGET KAPITAL OCH SKULDER'];
        if (ekTotal) {
            const ekRow = [{ content: 'SUMMA EGET KAPITAL OCH SKULDER', styles: { fontStyle: 'bold' } }];
            if (formalMode) {
                ekRow.push({ content: pdfVal(ekTotal.total_closing_balance, true), styles: { fontStyle: 'bold' } });
                if (hasPreviousYear) ekRow.push({ content: pdfVal(previousYearReport?.['EGET KAPITAL OCH SKULDER']?.total_closing_balance, true), styles: { fontStyle: 'bold' } });
            } else {
                ekRow.push(
                    { content: pdfVal(ekTotal.total_opening_balance, true), styles: { fontStyle: 'bold' } },
                    { content: pdfVal(ekTotal.total_change, true), styles: { fontStyle: 'bold' } },
                    { content: pdfVal(ekTotal.total_closing_balance, true), styles: { fontStyle: 'bold' } },
                );
            }
            doc.autoTable({ body: [ekRow], startY: doc.lastAutoTable.finalY, styles: { fontSize: 9 } });
        }

        // Balance equation check in PDF — only warn for closed years
        if (validation && !validation.balance_equation_valid && validation.fiscal_year_closed) {
            doc.setFontSize(10);
            doc.setTextColor(220, 53, 69);
            doc.text(`OBS: Balansräkningen går inte ihop. Differens: ${validation.difference} kr`, 14, doc.lastAutoTable.finalY + 8);
            doc.setTextColor(0, 0, 0);
        } else if (validation && Math.abs(validation.beraknat_resultat || 0) > 0.005) {
            doc.setFontSize(10);
            doc.text(`Beräknat resultat: ${pdfVal(validation.beraknat_resultat, false)} kr`, 14, doc.lastAutoTable.finalY + 8);
        }

        const suffix = selectedPeriod?.value?.type === 'month'
            ? `${selectedFiscalYear?.year}_${periodLabel.replace(/\s+/g, '-')}`
            : `${selectedFiscalYear?.year}`;
        doc.save(`Balansrapport_${suffix}.pdf`);
    };

    // -----------------------------------------------------------------------
    // Period selector value computation
    // -----------------------------------------------------------------------
    const currentSelectValue = (() => {
        const fyId = selectedFiscalYear?.fiscal_year_id;
        const p = selectedPeriod?.value;
        if (!fyId || !p) return null;
        for (const group of yearPeriodOptions) {
            for (const o of group.options) {
                const v = o.value;
                if (v.fiscalYear?.fiscal_year_id === fyId && v.period?.type === p.type &&
                    v.period?.startDate === p.startDate && v.period?.endDate === p.endDate) {
                    return o;
                }
            }
        }
        return null;
    })();

    // -----------------------------------------------------------------------
    // Loading / Error / Empty states (Task 4.4)
    // -----------------------------------------------------------------------
    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status" />
                <p className="mt-3">Laddar balansrapport...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                <Alert.Heading>Fel vid hämtning av data</Alert.Heading>
                <p>{error}</p>
            </Alert>
        );
    }

    if (fiscalYears.length === 0) {
        return (
            <>
                <PageHeader
                  icon={Scale}
                  title="Balansrapport"
                  subtitle="Tillgångar, eget kapital och skulder vid periodens slut."
                />
                <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
                <div className="text-center mt-4">
                    <Link to="#" onClick={() => history.push(`/${companyId}/settings/fiscal-years`)} className="btn btn-primary">
                        Gå till Inställningar
                    </Link>
                </div>
            </>
        );
    }

    // Guard: report not yet loaded
    if (!currentYearReport) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status" />
                <p className="mt-3">Laddar balansrapport...</p>
            </div>
        );
    }

    // Period dates for headers
    const periodStart = selectedPeriod?.value?.startDate || selectedFiscalYear?.start_date;
    const periodEnd = selectedPeriod?.value?.endDate || selectedFiscalYear?.end_date;

    // Column count: formal mode = 2-3 (name + UB + prev UB), working mode = 4 (name + IB + change + UB)
    const colCount = formalMode ? (hasPreviousYear ? 3 : 2) : 4;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div className="balance-report">
            <PageHeader
              icon={Scale}
              title="Balansrapport"
              subtitle="Tillgångar, eget kapital och skulder vid periodens slut."
            />

            {/* Controls bar */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <span>År/Period:</span>
                    <Select
                        value={currentSelectValue}
                        onChange={handleYearPeriodChange}
                        options={yearPeriodOptions}
                        styles={{
                            container: (base) => ({ ...base, minWidth: '300px' }),
                            menuList: (base) => ({ ...base, maxHeight: 280 }),
                        }}
                    />
                    <Form.Check
                        type="switch"
                        id="br-formal-switch"
                        label="Balansräkning ÅRL"
                        checked={formalMode}
                        onChange={() => setFormalMode(!formalMode)}
                        style={{ marginLeft: '1rem' }}
                    />
                    {!formalMode && (
                        <Form.Check
                            type="switch"
                            id="br-verifications-switch"
                            label="Visa verifikationer"
                            checked={showVerifications}
                            onChange={() => setShowVerifications(!showVerifications)}
                            style={{ marginLeft: '0.5rem' }}
                        />
                    )}
                </div>
                <Button variant="primary" onClick={exportToPDF}>
                    <FontAwesomeIcon icon={faFileDownload} className="me-2" />
                    Exportera till PDF
                </Button>
            </div>

            {/* Metadata bar (Task 4.6) */}
            {metadata && (
                <div className="metadata-bar">
                    Räkenskapsår: {metadata.fiscal_year_state === 'closed' ? 'Stängt' : 'Öppet'}
                    {metadata.ib_source && (
                        <> · IB-källa: {
                            metadata.ib_source === 'stored' ? 'Lagrad' :
                            metadata.ib_source === 'calculated_from_closed_year' ? 'Beräknad från stängt år' :
                            metadata.ib_source === 'calculated_from_open_year' ? 'Beräknad från öppet år (preliminär)' :
                            metadata.ib_source === 'calculated_at_date' ? 'Beräknad per datum' :
                            metadata.ib_source
                        }</>
                    )}
                    {metadata.generated_at && (
                        <> · Genererad: {new Date(metadata.generated_at).toLocaleString('sv-SE')}</>
                    )}
                </div>
            )}

            {/* Balance equation warning — only for closed fiscal years where it should be 0 */}
            {validation && !validation.balance_equation_valid && validation.fiscal_year_closed && (
                <Alert variant="danger" className="balance-warning">
                    <strong>Balansräkningen går inte ihop!</strong> Differens: {formatAmount(validation.difference)} kr.
                    Summa tillgångar: {formatAmount(validation.summa_tillgangar)},
                    Summa eget kapital och skulder: {formatAmount(validation.summa_eget_kapital_och_skulder)}.
                </Alert>
            )}

            {/* IB from open year warning */}
            {metadata?.ib_source === 'calculated_from_open_year' && (
                <Alert variant="warning">
                    Ingående balanser är beräknade från ett räkenskapsår som inte är stängt. Siffrorna kan vara preliminära.
                </Alert>
            )}

            <Card>
                <Card.Header>
                    <h5>{formalMode ? 'Balansräkning' : 'Balansrapport'}</h5>
                </Card.Header>
                <Card.Body style={{ overflowX: 'auto', padding: 0 }}>
                    <div style={{ minWidth: '800px' }}>
                        <Table style={{ tableLayout: 'fixed', width: '100%', marginBottom: '0' }}>
                            <colgroup>
                                {formalMode ? (
                                    <>
                                        <col style={{ width: '50%' }} />
                                        <col style={{ width: '25%' }} />
                                        {hasPreviousYear && <col style={{ width: '25%' }} />}
                                    </>
                                ) : (
                                    <>
                                        <col style={{ width: '40%' }} />
                                        <col style={{ width: '20%' }} />
                                        <col style={{ width: '20%' }} />
                                        <col style={{ width: '20%' }} />
                                    </>
                                )}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th></th>
                                    {formalMode ? (
                                        <>
                                            <th style={{ fontSize: '0.85em', fontWeight: 500 }}>
                                                <div>{periodEnd ? new Date(periodEnd).toLocaleDateString('sv-SE') : currentFiscalYearInfo?.year || ''}</div>
                                            </th>
                                            {hasPreviousYear && (
                                                <th style={{ fontSize: '0.85em', fontWeight: 500, color: '#6c757d' }}>
                                                    <div>{previousFiscalYearInfo?.end_date ? new Date(previousFiscalYearInfo.end_date).toLocaleDateString('sv-SE') : previousFiscalYearInfo?.year || ''}</div>
                                                </th>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <th style={{ fontSize: '0.85em', fontWeight: 500 }}>
                                                <div>Ingående balans</div>
                                                <div style={{ fontSize: '0.85em', color: '#999', fontWeight: 400 }}>
                                                    {periodStart ? new Date(periodStart).toLocaleDateString('sv-SE') : ''}
                                                </div>
                                            </th>
                                            <th style={{ fontSize: '0.85em', fontWeight: 500 }}>
                                                <div>Förändring</div>
                                            </th>
                                            <th style={{ fontSize: '0.85em', fontWeight: 500 }}>
                                                <div>Utgående balans</div>
                                                <div style={{ fontSize: '0.85em', color: '#999', fontWeight: 400 }}>
                                                    {periodEnd ? new Date(periodEnd).toLocaleDateString('sv-SE') : ''}
                                                </div>
                                            </th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {/* TILLGÅNGAR */}
                                {renderMainSection(currentYearReport, 'TILLGANGAR')}

                                {/* Spacer between main sections */}
                                <tr><td colSpan={colCount} style={{ height: '2.5rem', border: 'none', padding: 0 }}></td></tr>

                                {/* EGET KAPITAL OCH SKULDER */}
                                {renderMainSection(currentYearReport, 'EGET KAPITAL OCH SKULDER')}

                                {/* Empty row separator */}
                                <tr><td colSpan={colCount} style={{ height: '1rem', border: 'none' }}></td></tr>

                                {/* BERÄKNAT RESULTAT — shows year-to-date P&L for open years, 0 for closed */}
                                {!formalMode && validation && (
                                    <tr>
                                        <td><strong>BERÄKNAT RESULTAT</strong></td>
                                        <td><strong>{formatAmount(0)}</strong></td>
                                        <td><strong>{formatAmount(validation.beraknat_resultat || validation.difference)}</strong></td>
                                        <td>
                                            <strong style={{ color: !validation.balance_equation_valid ? '#dc3545' : undefined }}>
                                                {formatAmount(validation.beraknat_resultat || validation.difference)}
                                            </strong>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Verification Details Modal */}
            <VerificationDetailsModal
                showVerificationDetails={showVerificationModal}
                setShowVerificationDetails={setShowVerificationModal}
                selectedRow={selectedVerification}
                onVerificationUpdated={() => {
                    if (selectedFiscalYear && selectedPeriod) getBalanceReport();
                }}
                viewOnly={true}
            />
        </div>
    );
};

export default BalanceReport;
